import React, { useState, useEffect } from 'react';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { useNavigate } from 'react-router-dom';
import './ReportedCandidates.css';
import Swal from 'sweetalert2';
import { FaTrash, FaEye } from 'react-icons/fa';
import BaseUrl from '../../ApiServices/BaseUrl';

function ReportedCandidates() {
  const [reportedCandidates, setReportedCandidates] = useState([]);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch reported candidates
        const reportedResponse = await fetch(`${BaseUrl}/api/candidatereport/`);
        if (!reportedResponse.ok) {
          throw new Error('Failed to fetch reported candidates');
        }
        const reportedData = await reportedResponse.json();

        // Fetch job seekers
        const jobSeekerResponse = await fetch(`${BaseUrl}/api/job-seeker`);
        if (!jobSeekerResponse.ok) {
          throw new Error('Failed to fetch job seekers');
        }
        const jobSeekerData = await jobSeekerResponse.json();

        setReportedCandidates(reportedData);
        setJobSeekers(jobSeekerData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleReview = (candidateId) => {
    navigate(`/reportedcandidateview/${candidateId}`);
  };

  const handleView = (candidateId) => {
    navigate(`/reportedcandidateview/${candidateId}`);
  };

  const handleDelete = async (reportId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${BaseUrl}/api/candidatereport/${reportId}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          throw new Error('Failed to delete report');
        }

        // Remove the deleted report from state
        setReportedCandidates(reportedCandidates.filter(report => report.id !== reportId));

        Swal.fire(
          'Deleted!',
          'The report has been deleted.',
          'success'
        );
      } catch (err) {
        Swal.fire(
          'Error!',
          err.message,
          'error'
        );
      }
    }
  };

  // Check if a candidate exists in job seekers
  const isCandidateActive = (candidateId) => {
    return jobSeekers.some(seeker => seeker.user_id === candidateId);
  };

  // Get job seeker's verification status from database
  const getVerificationStatus = (candidateId) => {
    const jobSeeker = jobSeekers.find(seeker => seeker.user_id === candidateId);
    const status = jobSeeker ? jobSeeker.verification_status : null;

    // Map "Verified" status to "Active" for display
    if (status === 'Verified') {
      return 'Active';
    }
    return status;
  };

  // Get status for the new status column - directly from database
  const getStatus = (candidateId) => {
    const isActive = isCandidateActive(candidateId);
    const verificationStatus = getVerificationStatus(candidateId);

    if (!isActive) {
      return 'Inactive';
    } else if (verificationStatus === 'In Active') {
      return 'Reviewed';
    } else if (verificationStatus === 'Active') {
      return 'Active';
    } else {
      return 'Pending';
    }
  };

  // Get status class for styling based on verification_status from database
  const getStatusClass = (candidateId) => {
    const verificationStatus = getVerificationStatus(candidateId);
    const isActive = isCandidateActive(candidateId);

    if (!isActive) {
      return 'status-inactive';
    }

    switch (verificationStatus) {
      case 'Active':
        return 'status-active';
      case 'In Active':
        return 'status-reviewed';
      case 'Pending':
        return 'status-pending';
      default:
        return 'status-pending';
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavbar />
        <div style={{ marginTop: '100px' }}>Loading...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminNavbar />
        <div style={{ marginTop: '100px' }}>Error: {error}</div>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      <div className="reported-candidates-container">
        <h2>Reported Candidates</h2>
        {reportedCandidates.length > 0 ? (
          <div className="table-wrapper">
            <div className="table-responsive-reportedcandidates">
              <table className="reported-candidates-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Reported By</th>
                    <th>Reported Candidate</th>
                    <th className="reason-column">Reason</th>
                    <th className="description-column hide-on-mobile">Description</th>
                    <th className="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportedCandidates.map((candidate, index) => {
                    const isActive = isCandidateActive(candidate.candidate_id);
                    const verificationStatus = getVerificationStatus(candidate.candidate_id);
                    const status = getStatus(candidate.candidate_id);
                    const statusClass = getStatusClass(candidate.candidate_id);

                    return (
                      <tr key={candidate.id}>
                        <td>{index + 1}</td>
                        <td>{candidate.first_name}</td>
                        <td>{candidate.candidate_name}</td>
                        <td className="reason-column">{candidate.reason}</td>
                        <td className="description-column hide-on-mobile">{candidate.description}</td>
                        <td className="actions-column">
                          <div className="actions-container">
                            <button
                              className="view-btn"
                              onClick={() => handleView(candidate.candidate_id)}
                              title="View Candidate"
                            >
                              View
                            </button>

                            {!isActive ? (
                              <button className="removed-btn" disabled>
                                Removed
                              </button>
                            ) : verificationStatus === 'In Active' ? (
                              <button className="reviewed-btn" disabled>
                                Reviewed
                              </button>
                            ) : (
                              <button
                                className="review-btn"
                                onClick={() => handleReview(candidate.candidate_id)}
                              >
                                Review
                              </button>
                            )}

                            <span className={`report-status-badge ${statusClass}`}>
                              {verificationStatus || 'Pending'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p>No reported candidates found.</p>
        )}
      </div>
    </>
  );
}

export default ReportedCandidates;